import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui";
import type { CategoryOption } from "~/features/admin/relationTypes/types";
import { cn } from "~/lib/utils";

interface CategoryComboboxProps {
  options: CategoryOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder: string;
  ariaInvalid?: boolean;
}

/**
 * Searchable single-select picker for categories. Replaces V1's MUI
 * Autocomplete: type to filter by name, pick one option.
 */
export function CategoryCombobox({ options, value, onChange, placeholder, ariaInvalid }: CategoryComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const selected = options.find((option) => option.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          className="w-full justify-between font-normal"
        >
          <span className={cn("truncate", !selected && "text-fg-disabled")}>
            {selected ? selected.name : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>{t("ui.notFound")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.name}
                  onSelect={() => {
                    onChange(option.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("h-4 w-4", option.id === value ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{option.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
